import React, { useMemo, useRef, useState } from "react";
import { useCreate } from "@pankod/refine-core";
import {
  Alert,
  Button,
  Icons,
  Input,
  Modal,
  notificationProvider,
  Space,
  Table,
  Tag,
  Typography,
} from "@pankod/refine-antd";

import { parseVendorRows, SAMPLE_CSV } from "../parse";
import {
  BulkImportHiddenListedVendorResultRow,
  BulkImportHiddenListedVendorsForKodoPayOutput,
  BulkImportHiddenVendorRowStatus,
  ParsedVendorRow,
} from "../types";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const statusColor: Record<BulkImportHiddenVendorRowStatus, string> = {
  [BulkImportHiddenVendorRowStatus.CREATED]: "green",
  [BulkImportHiddenVendorRowStatus.SKIPPED_EXISTING]: "gold",
  [BulkImportHiddenVendorRowStatus.FAILED]: "red",
};

export const BulkImportHiddenVendorsModal: React.FC<{}> = () => {
  const [open, setOpen] = useState(false);
  const [rawText, setRawText] = useState("");
  const [result, setResult] = useState<BulkImportHiddenListedVendorsForKodoPayOutput | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate, isLoading } = useCreate();

  const parsedRows: ParsedVendorRow[] = useMemo(() => parseVendorRows(rawText), [rawText]);
  const invalidCount = useMemo(
    () => parsedRows.filter((r) => r.validationError).length,
    [parsedRows],
  );
  const canSubmit = parsedRows.length > 0 && invalidCount === 0 && !isLoading;

  const resetAll = () => {
    setRawText("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setRawText(text);
      setResult(null);
    } catch (err: any) {
      notificationProvider.open({
        message: err?.message || "Could not read the file",
        type: "error",
        description: "",
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    // Send exactly the columns the input DTO (BulkImportHiddenListedVendorRow) accepts.
    const vendors = parsedRows.map((r) => ({
      vendorName: r.vendorName,
      accountNumber: r.accountNumber,
      ifsc: r.ifsc,
      accountHolderName: r.accountHolderName,
      emailId: r.emailId,
      mobileNo: r.mobileNo,
      isVirtualAccount: r.isVirtualAccount,
    }));

    mutate(
      {
        resource: "pvtBulkImportHiddenListedVendorsForKodoPay",
        dataProviderName: "grapghQl",
        values: { input: { vendors } },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (response) => {
          const output = response?.data as unknown as BulkImportHiddenListedVendorsForKodoPayOutput;
          setResult(output);
          notificationProvider.open({
            message: "Import complete",
            type: "success",
            description: `Created ${output?.created ?? 0}, skipped ${output?.skipped ?? 0}, failed ${output?.failed ?? 0}`,
          });
        },
        onError: (err: any) => {
          notificationProvider.open({
            message: err?.message || "Bulk import failed",
            type: "error",
            description: "",
          });
        },
      },
    );
  };

  const previewColumns = [
    { title: "Line", dataIndex: "lineNo", key: "lineNo", width: 60 },
    { title: "Vendor Name", dataIndex: "vendorName", key: "vendorName" },
    { title: "Account Number", dataIndex: "accountNumber", key: "accountNumber" },
    { title: "IFSC", dataIndex: "ifsc", key: "ifsc" },
    { title: "Account Holder", dataIndex: "accountHolderName", key: "accountHolderName" },
    { title: "Email", dataIndex: "emailId", key: "emailId", render: (v: string) => v || "-" },
    { title: "Mobile", dataIndex: "mobileNo", key: "mobileNo", render: (v: string) => v || "-" },
    {
      title: "Virtual",
      dataIndex: "isVirtualAccount",
      key: "isVirtualAccount",
      render: (v: boolean) => (v ? <Tag color="blue">Virtual</Tag> : "-"),
    },
    {
      title: "Validation",
      key: "validation",
      render: (_: unknown, row: ParsedVendorRow) =>
        row.validationError ? (
          <Tag color="red">{row.validationError}</Tag>
        ) : (
          <Tag color="green">OK</Tag>
        ),
    },
  ];

  const resultColumns = [
    { title: "Vendor Name", dataIndex: "vendorName", key: "vendorName" },
    { title: "Account Number", dataIndex: "accountNumber", key: "accountNumber" },
    { title: "IFSC", dataIndex: "ifsc", key: "ifsc" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: BulkImportHiddenVendorRowStatus) => (
        <Tag color={statusColor[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (v: string) => v || "-",
    },
  ];

  return (
    <Space>
      <Button type="primary" onClick={() => setOpen(true)}>
        Bulk Import AMC Accounts
      </Button>

      <Modal
        title="Bulk Import Hidden Listed Vendors (AMC Collection Accounts)"
        visible={open}
        centered
        width={1000}
        onCancel={() => setOpen(false)}
        okText={`Import ${parsedRows.length} vendor(s)`}
        okButtonProps={{ disabled: !canSubmit, loading: isLoading }}
        onOk={handleSubmit}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="These accounts are imported as HIDDEN listed vendors."
          description="Paste rows or upload a CSV with columns: vendorName, accountNumber, ifsc, accountHolderName, emailId (optional), mobileNo (optional), isVirtualAccount (optional, true for virtual accounts such as MFU escrow — never paid via IMPS). Existing accounts are skipped by the backend; change their virtual flag from the accounts table."
        />

        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space>
            <Button
              icon={<Icons.UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
            <Button type="link" onClick={() => setRawText(SAMPLE_CSV)}>
              Load sample
            </Button>
            <Button type="link" danger onClick={resetAll}>
              Clear
            </Button>
          </Space>

          <TextArea
            rows={8}
            placeholder="Paste CSV rows here (header row optional)"
            value={rawText}
            onChange={(e) => {
              setRawText(e.target.value);
              setResult(null);
            }}
          />

          {parsedRows.length > 0 && (
            <>
              <Space>
                <Text strong>{parsedRows.length}</Text>
                <Text>row(s) parsed.</Text>
                {invalidCount > 0 ? (
                  <Tag color="red">{invalidCount} row(s) need fixing before import</Tag>
                ) : (
                  <Tag color="green">All rows valid</Tag>
                )}
              </Space>
              <Table
                size="small"
                rowKey="rowKey"
                dataSource={parsedRows}
                columns={previewColumns}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
              />
            </>
          )}

          {result && (
            <>
              <Paragraph style={{ marginBottom: 0 }}>
                <Space size="large">
                  <Text strong>Total: {result.totalRows}</Text>
                  <Tag color="green">Created: {result.created}</Tag>
                  <Tag color="gold">Skipped: {result.skipped}</Tag>
                  <Tag color="red">Failed: {result.failed}</Tag>
                </Space>
              </Paragraph>
              <Table
                size="small"
                rowKey={(row: BulkImportHiddenListedVendorResultRow) => `${row.accountNumber}-${row.ifsc}`}
                dataSource={result.results}
                columns={resultColumns}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
              />
            </>
          )}
        </Space>
      </Modal>
    </Space>
  );
};
